/**
 * The exercise ⋯ — three acts plus the move, each one a store operation, each refusal worded.
 *
 * ── One or two words and a drawing, since 8 September 2026 ───────────────────────────────────
 * Owner, from the running panel: the menu was a wall of text. Every entry printed a sentence
 * ("Połącz w superset z następnym") over two lines, with a second sentence under it explaining why
 * it could not act, and the move group printed "poniedziałek — Squat Session — obecny dzień" on top
 * of that. Three acts and a day list came to a paragraph and a half of prose the trainer had to read
 * every time they wanted to unlink a pair.
 *
 * So the entry is a verb and an icon. The two things the sentence carried are not gone, they moved:
 *
 * · **The refusal** is `aria-describedby`, pointing at a visually-hidden span beside the popup. The
 *   entry stays focusable and `aria-disabled` — Base UI's `disabled` sets exactly that and keeps the
 *   item in the roving sequence, which is what view ADR §12 asks for — so a screen reader still
 *   hears WHY, and the menu is still two words wide. `title` carries the same words for a pointer.
 * · **What the act did** is announced afterwards, from `MenuActionView.done`. It is not the
 *   accessible name: a name has to contain the visible label (WCAG 2.5.3) and Polish inflection
 *   makes that impossible — "Przenieś do rozgrzewki" does not contain "Rozgrzewka". The name is the
 *   label, and the sentence is live-region text, which has no such rule. See `exerciseMenuView`.
 * · **The move group's day** prints its bare weekday or badge and is NAMED with the workout standing
 *   on it, which does satisfy 2.5.3 — "Środa — Pull A" contains "Środa". See `exerciseMoveMenu`.
 *
 * The drawings are the mobile glyph registry's own, at this repo's house construction (24×24,
 * `fill: none`, `currentColor`, 2px round stroke, `aria-hidden`) — the same rule `EmptyStateIcons`
 * and `CalendarEventIcons` already follow, so a concept is one drawing on both platforms. Every one
 * of them is decorative: the verb beside it is what names the entry, never the picture.
 *
 * ── The two guards are subscribed to HERE ────────────────────────────────────────────────────
 * "May this be linked with the next?" and "may this be removed?" are PLAN-scoped questions —
 * grouping looks at the next row, removal looks at every day in the phase — so neither can be
 * answered from the day node the form renders. They are read through the entity's own narrow hooks,
 * one subscription per row, because there is no stable row count to hoist them to. Both hooks return
 * a value `Object.is` can compare, so a row re-renders only when its own answer changes.
 *
 * The WORDS are not here: `exerciseMenuView` composes them where a test can reach them.
 *
 * ── The way INTO the reorder is in here; the CROSS-DAY move is, again ────────────────────────
 * The menu carried "Przenieś w górę / w dół" and a "Przenieś do dnia…" group until 3 August 2026,
 * when the owner removed both: moving is drag and drop's job, and the keyboard lift covers the
 * reorder — arrows to aim, Space to drop, all through `runMoveCommand`. The lift started from a grip
 * until 15 September 2026, when the owner removed the grip; "Przestaw" now starts it and closes the
 * menu onto the block's row, where the arrows act. It is an entry into the lift, not up/down entries.
 *
 * The lift covers the reorder and nothing else. A block can also be DROPPED on another day's column
 * of the week behind the panel, and until 7 September 2026 that mechanic had no keyboard and no
 * menu route at all: the lift's commit hard-codes the block's own day. View ADR §12 requires a
 * non-pointer path dispatching the same command, so the day group is back, fed by the host's own
 * `dayTargets` and dispatching the identical `moveExercise` the drop does. `SessionMenu` on the
 * week canvas is the same construction one file away.
 */

import { useId, useRef, useState, type ComponentType, type RefObject } from 'react';

import { Menu } from '@base-ui/react/menu';

import {
  groupWithNext,
  removeExercise,
  setExerciseRole,
  unlinkSuperset,
  useCanGroupWithNext,
  useExerciseRemovalRefusal,
  useIsForkPlan,
  usePlanSchedulingMode,
} from '~/entities/plan-draft';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { useMenuLift, type MenuLift } from '~/shared/ui/dnd/useMenuLift';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import {
  exerciseMenuView,
  type ActionView,
  type ExerciseView,
  type Translate,
} from '../model/dayProgramming';
import type { ExerciseMoveMenuView } from '../model/exerciseMove';

import menuStyles from '~/shared/ui/MenuSurface.module.css';
import styles from './ExerciseMenu.module.css';

/** The house construction, shared by every drawing in this menu. */
const STROKE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** Mobile's `link` — two rings holding each other, its own mark for a pairing. */
function SupersetIcon({ className }: { readonly className: string }) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
    </svg>
  );
}

/**
 * The same `link`, struck through with `ban`'s own diagonal.
 *
 * The registry has no "unlink" glyph, and composing its two existing ones is the honest way to draw
 * the opposite of the entry above rather than inventing a third shape: a trainer reads the pairing
 * mark with a line through it without being taught anything.
 */
function UnlinkIcon({ className }: { readonly className: string }) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
      <path d="M4.5 4.5 19.5 19.5" />
    </svg>
  );
}

/** Mobile's `flame` — the warm-up, on both platforms. */
function WarmupIcon({ className }: { readonly className: string }) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M12 23a7 7 0 0 0 7-7c0-2.6-1.3-4.5-2.6-6-.3 1.1-1.1 1.9-2.1 1.9 1.3-3.1-.6-6-3-7.6.3 2.5-1.3 3.9-2.7 5.2C7 11 5 13 5 16a7 7 0 0 0 7 7z" />
    </svg>
  );
}

/** Mobile's `dumbbell` — the main work. */
function MainWorkIcon({ className }: { readonly className: string }) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M6.5 6.5 17.5 17.5" />
      <path d="m21 21-1-1" />
      <path d="m3 3 1 1" />
      <path d="m18 22 4-4" />
      <path d="m2 6 4-4" />
      <path d="m3 10 7-7" />
      <path d="m14 21 7-7" />
    </svg>
  );
}

/** Mobile's `arrows-up-down` — a position changing within one list. */
function ReorderIcon({ className }: { readonly className: string }) {
  return (
    <svg className={className} {...STROKE}>
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}

/** Mobile's `cal` — one day of the phase. */
function DayIcon({ className }: { readonly className: string }) {
  return (
    <svg className={className} {...STROKE}>
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </svg>
  );
}

/** The "Przenieś do dnia…" group and the dispatch behind it. `null` where there is no such act. */
export interface ExerciseDayMoveProps {
  readonly view: ExerciseMoveMenuView;
  /** Sends this exercise's BLOCK to that day, appending — the same command the drop dispatches. */
  readonly onMove: (daySlotToken: string, blockCount: number) => void;
}

interface ExerciseMenuProps {
  readonly exercise: ExerciseView;
  /** True when this exercise sits inside a superset or mini-group frame. */
  readonly isGrouped: boolean;
  /**
   * The cross-day move, or `null` where it is not offered — a viewer, and the day's standalone
   * warm-up run, which occupies no list position and is moved between blocks by the role entry
   * above rather than between days.
   */
  readonly move: ExerciseDayMoveProps | null;
  /**
   * The keyboard lift, started from here since the row lost its grip (owner, 15 September 2026).
   * `null` on a card that is not its block's address and wherever nothing may move.
   */
  readonly lift: MenuLift | null;
  /**
   * Where focus goes once the removal has been confirmed — the day's main-work list, handed down by
   * the form that owns it.
   *
   * Base UI returns focus to whatever opened the dialog, and after this particular confirm that
   * control is gone with the card it stood in: focus landed on `<body>`, so the next Tab restarted
   * at the top of the panel and nothing was announced. The day card's own delete already answers
   * this way (`pages/plan-builder/lib/useDayActions`, whose `restoreFocus` names the week panel);
   * this is the same answer one level down, and the list is where the change happened.
   */
  readonly removalFocus: RefObject<HTMLElement | null>;
  readonly t: Translate;
  readonly announce: (message: string) => void;
}

/**
 * One entry: a drawing, a verb, and — where it refuses — the reason, carried and never printed.
 *
 * The reason's span is rendered by the entry itself so the id and the text cannot drift apart, and
 * it is `srOnly` rather than `hidden`: `hidden` would take the sentence out of the accessibility
 * tree, which is the one thing it exists to be in.
 */
function Entry({
  action,
  icon,
  onSelect,
}: {
  readonly action: ActionView;
  readonly icon: ComponentType<{ readonly className: string }>;
  readonly onSelect: () => void;
}) {
  const reasonId = useId();
  const reason = action.disabledReason;
  const Icon = icon;
  return (
    <>
      <Menu.Item
        className={menuStyles.item}
        disabled={reason !== null}
        aria-describedby={reason === null ? undefined : reasonId}
        title={reason ?? undefined}
        onClick={() => {
          if (reason === null) onSelect();
        }}
      >
        <Icon className={String(styles.itemIcon)} />
        <span className={menuStyles.itemLabel}>{action.label}</span>
      </Menu.Item>
      {reason !== null && (
        <span id={reasonId} className={visuallyHidden.srOnly}>
          {reason}
        </span>
      )}
    </>
  );
}

export function ExerciseMenu({
  exercise,
  isGrouped,
  move,
  lift,
  removalFocus,
  t,
  announce,
}: ExerciseMenuProps) {
  const canGroupWithNext = useCanGroupWithNext(exercise.slotToken);
  const removalRefusal = useExerciseRemovalRefusal(exercise.slotToken);
  const groupReasonId = useId();
  const menuLift = useMenuLift(lift);
  // A running client plan confirms the removal first, as the phone does; a library template does not
  // — there is no client downstream of it. The unit noun follows the plan's own scheduling mode.
  const isFork = useIsForkPlan();
  const isSelfPaced = usePlanSchedulingMode() === 'selfpaced';
  const [confirming, setConfirming] = useState(false);
  /* Where the confirm hands focus back, decided by WHICH answer it got.
   *
   * One ref rather than two dialogs: the cancel leaves the trash standing and belongs on it, the
   * confirm takes the trash away with the card and belongs on the list. Both are written before the
   * close commits, which is before Base UI reads the target. */
  const dialogFocus = useRef<HTMLElement | null>(null);
  const trashRef = useRef<HTMLButtonElement>(null);

  const menu = exerciseMenuView(t, exercise, {
    canGroupWithNext,
    isGrouped,
    removalRefusal,
    live: isFork ? { unitIsCycle: isSelfPaced } : null,
  });
  const act = (message: string, run: () => void) => {
    run();
    announce(message);
  };

  return (
    <>
    <Menu.Root>
      <Menu.Trigger className={styles.trigger} aria-label={exercise.menuLabel}>
        <span aria-hidden="true">⋯</span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
          <Menu.Popup
            className={menuStyles.popup}
            finalFocus={menuLift.finalFocus}
          >
            <Entry
              action={menu.group}
              icon={SupersetIcon}
              onSelect={() => {
                act(menu.group.done, () => {
                  groupWithNext(exercise.slotToken);
                });
              }}
            />
            <Entry
              action={menu.ungroup}
              icon={UnlinkIcon}
              onSelect={() => {
                act(menu.ungroup.done, () => {
                  unlinkSuperset(exercise.slotToken);
                });
              }}
            />
            <span className={menuStyles.separator} />
            <Entry
              action={menu.toggleRole}
              icon={exercise.isWarmup ? MainWorkIcon : WarmupIcon}
              onSelect={() => {
                act(menu.toggleRole.done, () => {
                  setExerciseRole(exercise.slotToken, exercise.isWarmup ? 'Main' : 'Warmup');
                });
              }}
            />
            {(lift !== null || move !== null) && <span className={menuStyles.separator} />}
            {lift !== null && (
              <Entry
                action={lift}
                icon={ReorderIcon}
                onSelect={menuLift.select}
              />
            )}
            {move !== null && (
              <>
                <Menu.Group>
                  <Menu.GroupLabel className={menuStyles.groupLabel}>
                    {move.view.label}
                  </Menu.GroupLabel>
                  {/* One reason for the WHOLE group — a consumed phase, or a phase holding this day
                    * alone — stated once and pointed at by every stop, exactly as the view words it.
                    * A per-stop copy would have a keyboard trainer hear the same sentence seven
                    * times walking the list. */}
                  {move.view.disabledReason !== null && (
                    <span id={groupReasonId} className={visuallyHidden.srOnly}>
                      {move.view.disabledReason}
                    </span>
                  )}
                  {move.view.targets.map((target) => {
                    const reason = move.view.disabledReason ?? target.disabledReason;
                    return (
                      <DayEntry
                        key={target.slotToken}
                        label={target.label}
                        name={target.name}
                        reason={reason}
                        // The group's refusal already has a span; only a stop's OWN reason needs one.
                        sharedReasonId={
                          move.view.disabledReason === null ? null : groupReasonId
                        }
                        onSelect={() => {
                          move.onMove(target.slotToken, target.blockCount);
                        }}
                      />
                    );
                  })}
                </Menu.Group>
              </>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>

    {/* The mock draws a 🗑 beside the ⋯, so removal came OUT of the menu on 8 September 2026 rather
      * than being drawn in both places: a card with two paths to its one destructive act is the
      * defect the entry was moved to avoid, and the mock decides which of the two survives.
      *
      * It reads the same refusal the entry did, from the same subscription — which is why the two
      * controls are one component. Refused, it keeps its place in the tab order and carries the
      * reason in its NAME, because an icon button has no other words. */}
    <button
      ref={trashRef}
      type="button"
      className={styles.remove}
      aria-label={
        menu.remove.disabledReason === null
          ? menu.remove.name
          : `${menu.remove.name}, ${menu.remove.disabledReason}`
      }
      aria-disabled={menu.remove.disabledReason !== null ? 'true' : undefined}
      title={menu.remove.disabledReason ?? menu.remove.label}
      onClick={() => {
        if (menu.remove.disabledReason !== null) return;
        if (menu.remove.confirm !== null) {
          dialogFocus.current = trashRef.current;
          setConfirming(true);
          return;
        }
        act(menu.remove.done, () => {
          removeExercise(exercise.slotToken);
        });
      }}
    >
      <svg
        viewBox="0 0 24 24"
        className={styles.removeIcon}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M5 7h14M10 7V5h4v2M8 7l1 13h6l1-13" />
      </svg>
    </button>

    {/* Mounted per row, like the set table's own column-removal confirm one file over: `prompt` is
      * null until the trash is pressed, and only one row can be waiting on an answer at a time. */}
    <ConfirmDialog
      prompt={confirming ? menu.remove.confirm : null}
      finalFocus={dialogFocus}
      onConfirm={() => {
        // The card this dialog was raised from is about to go, and the trash with it. See
        // `removalFocus`: the list is what stays, and it is where the change happened.
        dialogFocus.current = removalFocus.current;
        setConfirming(false);
        act(menu.remove.done, () => {
          removeExercise(exercise.slotToken);
        });
      }}
      onCancel={() => {
        setConfirming(false);
      }}
    />
    </>
  );
}

/**
 * One stop of the move group.
 *
 * It PRINTS the day's bare identity and is NAMED with the workout standing on it — the two are
 * different strings, so the name is stated rather than left to the item's contents. `aria-label`
 * is legitimate here where it is not on a bare `<span>`: a `menuitem` has a role that supports it.
 */
function DayEntry({
  label,
  name,
  reason,
  sharedReasonId,
  onSelect,
}: {
  readonly label: string;
  readonly name: string;
  readonly reason: string | null;
  readonly sharedReasonId: string | null;
  readonly onSelect: () => void;
}) {
  const ownReasonId = useId();
  const describedBy = reason === null ? undefined : (sharedReasonId ?? ownReasonId);
  return (
    <>
      <Menu.Item
        className={menuStyles.item}
        aria-label={name}
        disabled={reason !== null}
        aria-describedby={describedBy}
        title={reason ?? name}
        onClick={() => {
          if (reason === null) onSelect();
        }}
      >
        <DayIcon className={String(styles.itemIcon)} />
        <span className={menuStyles.itemLabel}>{label}</span>
      </Menu.Item>
      {reason !== null && sharedReasonId === null && (
        <span id={ownReasonId} className={visuallyHidden.srOnly}>
          {reason}
        </span>
      )}
    </>
  );
}
